import React, { memo, useState } from 'react';
import { generateWhatsAppLink } from '../utils/whatsapp';

interface CardProps {
  item: any;
}

export const BusinessCard: React.FC<CardProps> = memo(({ item }) => {
  const [imageError, setImageError] = useState(false);

  const imageUrl = item.thumbnailUrl || item.thumbanilUrl || item.thumbNailUrl || '';

  // Generate initials for placeholder
  const getInitials = (title: string) => {
    return title
      .split(' ')
      .filter((w: string) => w.length > 0)
      .slice(0, 2)
      .map((w: string) => w[0].toUpperCase())
      .join('');
  };

  return (
    <div className="group bg-white border border-gray-100 rounded-3xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-300 ease-out flex flex-col h-full overflow-hidden">
      {/* Image Section */}
      <div className="relative h-48 bg-gray-50 flex items-center justify-center overflow-hidden">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={item.title}
            decoding="async"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImageError(true)}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-out"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-2">
              <span className="text-xl font-bold text-blue-600">{getInitials(item.title || 'NA')}</span>
            </div>
            <span className="text-xs font-medium text-gray-400">{item.type || 'Business'}</span>
          </div>
        )}
        
        {(item.rating || item.ratingCount) && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm flex items-center gap-1 border border-gray-100">
            <span className="text-yellow-500 text-sm">★</span>
            <span className="text-gray-900 text-sm font-bold">{item.rating || 'N/A'}</span>
            <span className="text-gray-400 text-xs">({item.ratingCount || 0})</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex-1 p-6 flex flex-col gap-3">
        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors">
          {item.title}
        </h3>

        {/* Address */}
        {item.address && (
          <div className="flex items-start gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="line-clamp-2">{item.address}</span>
          </div>
        )}

        {/* Phone */}
        {item.phoneNumber && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>{item.phoneNumber}</span>
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-4 border-t border-gray-50 flex items-center gap-2 w-full">
          {/* Website Button */}
          <a
            href={item.website || '#'}
            target="_blank"
            rel="noopener noreferrer"
            title={item.website || 'No website available'}
            className={`flex-1 flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2.5 rounded-xl transition-colors ${
              item.website 
                ? 'text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 cursor-pointer' 
                : 'text-gray-400 bg-gray-100 cursor-not-allowed pointer-events-none'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span>{item.website ? 'Visit' : 'No Site'}</span>
          </a>

          {/* WhatsApp Button */}
          <a
            href={item.phoneNumber ? generateWhatsAppLink(item.phoneNumber, item.title) : '#'}
            target="_blank"
            rel="noopener noreferrer"
            title={item.phoneNumber ? 'Message on WhatsApp' : 'No phone available'}
            className={`flex-1 flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2.5 rounded-xl transition-colors ${
              item.phoneNumber 
                ? 'text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 cursor-pointer' 
                : 'text-gray-400 bg-gray-100 cursor-not-allowed pointer-events-none'
            }`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421-7.403h-.004a9.87 9.87 0 00-9.746 9.798c0 2.718.738 5.33 2.14 7.59l-2.275 6.821 7.043-2.065a9.857 9.857 0 004.838 1.231h.004c5.442 0 9.884-4.418 9.884-9.84 0-2.63-.898-5.098-2.475-7.129-1.577-2.03-3.779-3.461-6.256-3.761z" />
            </svg>
            <span>{item.phoneNumber ? 'Message' : 'No Phone'}</span>
          </a>
        </div>
      </div>
    </div>
  );
});

BusinessCard.displayName = 'BusinessCard';
export default BusinessCard;
